import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, DatePicker, Empty, Form, Input, Modal, Popconfirm, Select,
  Space, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  DeleteOutlined, DownloadOutlined, EditOutlined, FileExcelOutlined, PlusOutlined, WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  createTraveler, deleteTraveler, getParticipants, getTravelers, exportTravelersCsv,
  exportTravelersExcel, updateTraveler,
} from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';

const { Text } = Typography;

const DUTY = {
  student: { label: 'Öğrenci',  color: 'blue' },
  parent:  { label: 'Veli',     color: 'purple' },
  teacher: { label: 'Öğretmen', color: 'green' },
  other:   { label: 'Diğer',    color: 'default' },
};

const StageTravelersTab = ({ stage }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, missing_passport: 0, expired_passport: 0 });
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, record: null });
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: ps }] = await Promise.all([
        getTravelers(stage.id),
        getParticipants(stage.id, { per_page: 100 }),
      ]);
      setRows(data.data || []);
      setSummary(data.summary || {});
      setParticipants(ps.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Pasaport listesi yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [stage.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const payload = {
        ...v,
        birth_date: v.birth_date ? v.birth_date.format('YYYY-MM-DD') : null,
        passport_expiry: v.passport_expiry ? v.passport_expiry.format('YYYY-MM-DD') : null,
      };
      if (modal.record) {
        await updateTraveler(modal.record.id, payload);
        message.success('Güncellendi.');
      } else {
        await createTraveler(stage.id, payload);
        message.success('Kişi eklendi.');
      }
      setModal({ open: false, record: null });
      form.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  const expiredForStage = (r) =>
    r.passport_expiry && stage.end_date && dayjs(r.passport_expiry).isBefore(dayjs(stage.end_date));

  const columns = [
    { title: 'Ad Soyad', dataIndex: 'full_name' },
    {
      title: 'Görev',
      dataIndex: 'duty',
      width: 110,
      render: (v) => <Tag color={DUTY[v]?.color}>{DUTY[v]?.label || v}</Tag>,
    },
    {
      title: 'Bağlı Öğrenci',
      render: (_, r) => {
        const s = r.participant?.student;
        return s ? `${s.name} ${s.surname}` : <Text type="secondary">Kurum personeli</Text>;
      },
    },
    {
      title: 'Doğum Tarihi',
      dataIndex: 'birth_date',
      width: 120,
      render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—'),
    },
    { title: 'TC / Kimlik', dataIndex: 'id_number', width: 130, render: (v) => v || '—' },
    {
      title: 'Pasaport No',
      dataIndex: 'passport_no',
      width: 130,
      render: (v) => v || <Tag color="warning">Eksik</Tag>,
    },
    {
      title: 'Geçerlilik',
      dataIndex: 'passport_expiry',
      width: 140,
      render: (v, r) => {
        if (!v) return <Tag color="warning">Eksik</Tag>;
        const txt = dayjs(v).format('DD.MM.YYYY');
        return expiredForStage(r)
          ? <Tooltip title="Etap bitiş tarihinden önce doluyor"><Tag color="error" icon={<WarningOutlined />}>{txt}</Tag></Tooltip>
          : txt;
      },
    },
    {
      title: 'Giren',
      dataIndex: 'filled_by',
      width: 90,
      render: (v) => <Tag color={v === 'parent' ? 'blue' : 'default'}>{v === 'parent' ? 'Veli' : 'Yönetici'}</Tag>,
    },
    {
      title: 'İşlemler',
      width: 110,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setModal({ open: true, record: r });
              form.setFieldsValue({
                ...r,
                birth_date: r.birth_date ? dayjs(r.birth_date) : null,
                passport_expiry: r.passport_expiry ? dayjs(r.passport_expiry) : null,
              });
            }}
          />
          <Popconfirm
            title="Kişi çıkarılsın mı?"
            onConfirm={async () => {
              try {
                await deleteTraveler(r.id);
                message.success('Çıkarıldı.');
                load();
              } catch (e) { message.error(apiErrorMessage(e)); }
            }}
            okText="Çıkar"
            cancelText="Vazgeç"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Iki bicim ayni ucu paylasiyor; yalnizca dosya tipi degisiyor.
  const indir = async (bicim) => {
    const excel = bicim === 'excel';
    try {
      const res = excel ? await exportTravelersExcel(stage.id) : await exportTravelersCsv(stage.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pasaport-listesi-${stage.id}.${excel ? 'xlsx' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error(apiErrorMessage(err, excel ? 'Excel indirilemedi.' : 'CSV indirilemedi.'));
    }
  };

  return (
    <div>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="Hassas veri"
        description="Bu listede TC ve pasaport bilgileri var. Velilerin çoğu bu bilgileri uygulamadan kendisi girer; buradan yalnızca eksikleri tamamlayın."
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Tag>{summary.total || 0} kişi</Tag>
        {summary.missing_passport > 0 && <Tag color="warning">{summary.missing_passport} eksik pasaport</Tag>}
        {summary.expired_passport > 0 && <Tag color="error">{summary.expired_passport} süresi yetersiz</Tag>}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setModal({ open: true, record: null }); form.setFieldsValue({ duty: 'student' }); }}
        >
          Kişi Ekle
        </Button>
        <Tooltip title="Aracı firmaya gönderilecek tabloyu sütunlara ayrılmış Excel dosyası olarak indirir">
          <Button
            type="primary"
            ghost
            icon={<FileExcelOutlined />}
            onClick={() => indir('excel')}
          >
            Excel İndir
          </Button>
        </Tooltip>
        <Tooltip title="Aynı tablo, düz metin (CSV) olarak. Excel'de tek sütunda açılırsa Excel İndir'i kullanın.">
          <Button icon={<DownloadOutlined />} onClick={() => indir('csv')}>
            CSV İndir
          </Button>
        </Tooltip>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        size="small"
        scroll={{ x: 1100 }}
        pagination={false}
        locale={{ emptyText: <Empty description="Henüz kişi girilmemiş." /> }}
      />

      <Modal
        title={modal.record ? 'Kişiyi Düzenle' : 'Kişi Ekle'}
        open={modal.open}
        onOk={submit}
        onCancel={() => { setModal({ open: false, record: null }); form.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="full_name" label="Ad Soyad" rules={[{ required: true, message: 'Ad soyad girin.' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="duty" label="Görev">
            <Select options={Object.entries(DUTY).map(([k, v]) => ({ value: k, label: v.label }))} />
          </Form.Item>
          <Form.Item
            name="competition_participant_id"
            label="Bağlı Öğrenci"
            extra="Refakatçi öğretmen/kurum personeli için boş bırakın."
          >
            <Select
              allowClear
              placeholder="Hangi öğrencinin katılımına bağlı"
              options={participants.map((p) => ({
                value: p.id,
                label: `${p.student?.name || ''} ${p.student?.surname || ''}`.trim(),
              }))}
            />
          </Form.Item>
          <Space>
            <Form.Item name="birth_date" label="Doğum Tarihi"><DatePicker format="DD.MM.YYYY" /></Form.Item>
            <Form.Item name="id_number" label="TC / Kimlik No"><Input maxLength={20} /></Form.Item>
          </Space>
          <Space>
            <Form.Item name="passport_no" label="Pasaport No"><Input maxLength={30} /></Form.Item>
            <Form.Item name="passport_expiry" label="Geçerlilik"><DatePicker format="DD.MM.YYYY" /></Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default StageTravelersTab;
